import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Table from '@mui/joy/Table';
import ToggleButtonGroup from '@mui/joy/ToggleButtonGroup';
import Tooltip from '@mui/joy/Tooltip';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import CircularProgress from '@mui/joy/CircularProgress';
import Input from '@mui/joy/Input';
import Switch from '@mui/joy/Switch';
import BugReportIcon from '@mui/icons-material/BugReport';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { listCallsForAgent, listAllCalls, getAgent, getCallTransactionLog, getCallRecordingBlob, getCallInvocationLog } from '../../api/agent';
import Transcript from '../agent/Transcript';
import { getCallDiagnostics } from '../sets/testDiagnostics';

function formatPresetDate(d) {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

// Single-day options offered in the Date range dropdown: today, yesterday,
// then each of the preceding 5 days (labelled by their actual date), and
// finally Custom. Day presets use the day offset (in days) from today as their
// value (0 = today, negative = days in the past).
function buildDatePresets(now) {
  const presets = [
    { value: '0', label: 'Today' },
    { value: '-1', label: 'Yesterday' },
  ];
  for (let i = 2; i <= 6; i += 1) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    presets.push({ value: String(-i), label: formatPresetDate(d) });
  }
  presets.push({ value: 'custom', label: 'Custom' });
  return presets;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function formatDdMm(d) {
  return `${padNum(d.getDate(), 2)}/${padNum(d.getMonth() + 1, 2)}`;
}

function formatHhMm(d) {
  return `${padNum(d.getHours(), 2)}:${padNum(d.getMinutes(), 2)}`;
}

function isDefaultStartTime(d) {
  return d.getHours() === 0 && d.getMinutes() === 0;
}

function isDefaultEndTime(d) {
  // Treat 23:59 and 23:59:59 as "default end".
  return d.getHours() === 23 && d.getMinutes() === 59;
}

function formatCustomRangeCondensed(start, end) {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return '';
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return '';

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const omitTimes = isDefaultStartTime(start) && isDefaultEndTime(end);

  if (sameDay) {
    if (omitTimes) return `${formatDdMm(start)}`;
    return `${formatDdMm(start)} ${formatHhMm(start)}→${formatHhMm(end)}`;
  }

  if (omitTimes) return `${formatDdMm(start)}→${formatDdMm(end)}`;
  return `${formatDdMm(start)}:${formatHhMm(start)}→${formatDdMm(end)}:${formatHhMm(end)}`;
}

function toDatetimeLocalValue(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = padNum(d.getMonth() + 1, 2);
  const dd = padNum(d.getDate(), 2);
  const hh = padNum(d.getHours(), 2);
  const min = padNum(d.getMinutes(), 2);
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

function fromDatetimeLocalValue(v, fallback) {
  const s = String(v || '').trim();
  if (!s) return fallback;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? fallback : d;
}

function toDateInputValue(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = padNum(d.getMonth() + 1, 2);
  const dd = padNum(d.getDate(), 2);
  return `${yyyy}-${mm}-${dd}`;
}

function toTimeInputValue(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${padNum(d.getHours(), 2)}:${padNum(d.getMinutes(), 2)}`;
}

function setDatePart(base, yyyyMmDd) {
  const b = new Date(base);
  const s = String(yyyyMmDd || '').trim();
  if (!s) return b;
  const d = new Date(`${s}T00:00`);
  if (Number.isNaN(d.getTime())) return b;
  b.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
  return b;
}

function setTimePart(base, hhmm, { endOfMinute } = { endOfMinute: false }) {
  const b = new Date(base);
  const s = String(hhmm || '').trim();
  if (!s) return b;
  const m = s.match(/^(\d{2}):(\d{2})$/);
  if (!m) return b;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return b;
  b.setHours(hh, mm, endOfMinute ? 59 : 0, endOfMinute ? 999 : 0);
  return b;
}

function formatDateTime(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleString();
}

function formatMobileDateTimeShort(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  const dd = padNum(d.getDate(), 2);
  const mm = padNum(d.getMonth() + 1, 2);
  const hh = padNum(d.getHours(), 2);
  const min = padNum(d.getMinutes(), 2);
  return `${dd}/${mm}:${hh}:${min}`;
}

function formatDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return '–';
  const s = new Date(startedAt).getTime();
  const e = new Date(endedAt).getTime();
  const secs = Math.round((e - s) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s_ = secs % 60;
  return `${m}:${String(s_).padStart(2, '0')}`;
}

function callStartedAtMs(call) {
  if (!call?.startedAt) return null;
  const t = new Date(call.startedAt).getTime();
  return Number.isFinite(t) ? t : null;
}

function callDurationSeconds(call) {
  const s = call?.startedAt ? new Date(call.startedAt).getTime() : NaN;
  const e = call?.endedAt ? new Date(call.endedAt).getTime() : NaN;
  if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return null;
  return Math.round((e - s) / 1000);
}

function padNum(n, len) {
  return String(n).padStart(len, '0');
}

/** Local wall-clock time with milliseconds, no date (debug log rows). */
function formatLocalTimeHmsMs(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return '–';
  return `${padNum(d.getHours(), 2)}:${padNum(d.getMinutes(), 2)}:${padNum(d.getSeconds(), 2)}.${padNum(d.getMilliseconds(), 3)}`;
}

/** Local calendar date + time with milliseconds (debug entry popout). */
function formatLocalDateTimeHmsMs(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return '–';
  const datePart = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  return `${datePart} ${formatLocalTimeHmsMs(ms)}`;
}

/** Strip surrounding single or double quote characters from a string (e.g. from JSON stringification). */
function stripQuotes(s) {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    return t.slice(1, -1);
  }
  return s;
}

/**
 * Get timestamp in ms for a single log entry (OTLP timeUnixNano or pino .time).
 */
function entryTimeMs(entry) {
  if (entry == null) return 0;
  if (entry.timeUnixNano != null) return Number(entry.timeUnixNano) / 1e6;
  if (typeof entry.time === 'number') return entry.time;
  // Pino / GCP style timestamp: either an ISO string, or an object { seconds, nanos }
  if (entry.timestamp != null) {
    if (typeof entry.timestamp === 'string') {
      const t = Date.parse(entry.timestamp);
      if (Number.isFinite(t)) return t;
    } else if (
      typeof entry.timestamp === 'object' &&
      entry.timestamp !== null &&
      typeof entry.timestamp.seconds === 'number'
    ) {
      const seconds = entry.timestamp.seconds || 0;
      const nanos = typeof entry.timestamp.nanos === 'number' ? entry.timestamp.nanos : 0;
      return seconds * 1000 + nanos / 1e6;
    }
  }
  // Fallback: some logs may use a string `time` field
  if (typeof entry.time === 'string') {
    const t = Date.parse(entry.time);
    if (Number.isFinite(t)) return t;
  }
  if (entry.observedTimeUnixNano != null) return Number(entry.observedTimeUnixNano) / 1e6;
  return 0;
}

/**
 * Merge and sort all individual log entries from invocation log API records.
 * Each API record is a subsystem with record.log.logs (array). Entries may be OTLP (timeUnixNano) or pino (.time).
 * Returns sorted array of { timeMs, subsystem, entry }.
 */
function decodeInvocationLogRecords(records) {
  if (!Array.isArray(records) || records.length === 0) return [];
  const merged = [];
  for (const rec of records) {
    const subsystem = rec?.subsystem ?? '';
    const log = rec?.log;
    const logs = log && typeof log === 'object' && Array.isArray(log.logs) ? log.logs : [];
    for (const entry of logs) {
      merged.push({
        timeMs: entryTimeMs(entry),
        subsystem,
        entry,
      });
    }
  }
  merged.sort((a, b) => a.timeMs - b.timeMs);
  return merged;
}

/** One-line summary of a decoded log entry (OTLP body.stringValue or pino msg). */
function decodedEntrySummary(item) {
  const e = item?.entry;
  if (e == null) return '–';
  if (e.body?.stringValue != null) return String(e.body.stringValue).slice(0, 120);
  if (e.msg != null) return String(e.msg).slice(0, 120);
  if (e.message != null) return String(e.message).slice(0, 120);
  try {
    return JSON.stringify(e).slice(0, 120);
  } catch {
    return '–';
  }
}

/** Map transaction log records to transcript shape and per-segment start offsets (seconds from call start). Includes call, answer, user, user-aux (the auxiliary STT's reading of the caller), agent, agent-speech (the output audit's reading of the agent's own audio), hangup/goodbye, call_failed. Prefers isFinal for user/agent; always includes system events (call, answer, hangup, goodbye). */
function transactionLogToTranscript(records, callStartedAt) {
  if (!Array.isArray(records) || records.length === 0) return { entries: [], offsets: [] };
  const startMs = callStartedAt ? new Date(callStartedAt).getTime() : 0;
  const systemTypes = ['call', 'answer', 'hangup', 'goodbye', 'call_failed'];
  const withFinal = records.filter((r) => r.isFinal);
  const conversationList = withFinal.length > 0 ? withFinal : records;
  const systemEvents = records.filter((r) => systemTypes.includes(r?.type));
  const merged = [...conversationList.filter((r) => !systemTypes.includes(r?.type)), ...systemEvents].sort(
    (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0)
  );
  const entries = [];
  const offsets = [];
  for (const rec of merged) {
    const type = rec?.type;
    let raw = rec?.data;
    if (typeof raw === 'string') raw = stripQuotes(raw);
    const offsetSec = rec?.createdAt ? (new Date(rec.createdAt).getTime() - startMs) / 1000 : 0;
    const push = (entry) => { entries.push(entry); offsets.push(offsetSec); };

    if (type === 'call') {
      const value = typeof raw === 'string' ? raw : (raw?.text ?? raw?.msg ?? String(raw ?? ''));
      if (value) push({ call: value });
      continue;
    }
    if (type === 'answer') {
      const value = typeof raw === 'string' ? raw : (raw?.text ?? raw?.msg ?? String(raw ?? ''));
      push({ inject: value ? `Call answered: ${value}` : 'Call answered' });
      continue;
    }
    if (type === 'hangup' || type === 'goodbye') {
      const value = typeof raw === 'string' ? raw : (raw?.text ?? raw?.msg ?? String(raw ?? ''));
      push({ hangup: value || true });
      continue;
    }
    if (type === 'call_failed') {
      const value = typeof raw === 'string' ? raw : (raw?.text ?? raw?.msg ?? String(raw ?? ''));
      push({ call_failed: value || 'Call failed' });
      continue;
    }
    if (type === 'function_results') {
      let parsed = raw;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          parsed = null;
        }
      }
      const list = Array.isArray(parsed) ? parsed : (parsed && typeof parsed === 'object' && Array.isArray(parsed.function_results) ? parsed.function_results : []);
      for (const item of list) {
        const name = item?.name != null ? String(item.name) : '';
        const resultRaw = item?.result;
        if (!name) continue;
        let resultText = '';
        if (typeof resultRaw === 'string') resultText = stripQuotes(resultRaw);
        else if (resultRaw == null) resultText = '';
        else {
          try {
            resultText = JSON.stringify(resultRaw);
          } catch {
            resultText = String(resultRaw);
          }
        }
        push({ inject: `${name}${resultText ? `: ${resultText}` : ''}` });
      }
      continue;
    }
    if (type === 'user' || type === 'agent' || type === 'user-aux' || type === 'agent-speech') {
      let text = null;
      if (typeof raw === 'string' && raw.trim()) text = raw;
      else if (raw && typeof raw === 'object' && typeof raw.text === 'string') text = stripQuotes(raw.text);
      else if (raw && typeof raw === 'object' && typeof raw.msg === 'string') text = stripQuotes(raw.msg);
      if (text == null) continue;
      if (type === 'user') push({ user: text });
      else if (type === 'user-aux') push({ 'user-aux': text });
      else if (type === 'agent-speech') push({ 'agent-speech': text });
      else push({ agent: text });
    }
  }
  return { entries, offsets };
}

export default function ObservePanel({ selectedAgentId, setSelectedAgentId, setError, observeCallId, setObserveCallId, onTroubleshoot }) {
  const CALLS_PAGE_SIZE = 20;
  const MAX_CALLS_TO_DISPLAY = 500;
  const datePresets = useMemo(() => buildDatePresets(new Date()), []);
  const [datePreset, setDatePreset] = useState('0');
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [customStart, setCustomStart] = useState(() => startOfDay(new Date()));
  const [customEnd, setCustomEnd] = useState(() => endOfDay(new Date()));
  const [hasAppliedCustom, setHasAppliedCustom] = useState(false);
  const prevNonCustomPresetRef = useRef('0');
  const [callScope, setCallScope] = useState(() => (selectedAgentId ? 'agent' : 'all')); // 'agent' | 'all'
  // Tracks whether the user explicitly changed the scope switch. If not, scope auto-follows selectedAgentId:
  // - no agent: default to 'all'
  // - agent loaded: default to 'agent'
  const callScopeUserTouchedRef = useRef(false);
  const [currentAgentLabel, setCurrentAgentLabel] = useState('');
  const [agentNameById, setAgentNameById] = useState(() => new Map());
  const [calls, setCalls] = useState([]);
  const [sort, setSort] = useState(() => ({ field: 'startedAt', order: 'desc' })); // field: 'startedAt' | 'duration', order: 'asc' | 'desc'
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [troubleshootingId, setTroubleshootingId] = useState(null); // call id whose diagnostics are being gathered for "Troubleshoot with AI"
  const [expandedGroups, setExpandedGroups] = useState(() => new Set()); // root ids of expanded transfer groups
  /** Full transcript and offsets from transaction log; visible portion is derived by currentTime when audio is present. */
  const [fullTranscriptEntries, setFullTranscriptEntries] = useState([]);
  const [fullTranscriptOffsets, setFullTranscriptOffsets] = useState([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(null);
  /** True once user has clicked play on the recording; then transcript switches to timecode-based. */
  const [playbackStarted, setPlaybackStarted] = useState(false);
  /** True while the audio element is actually playing (false when paused or ended); used to enable manual scroll sync when paused. */
  const [isPlaying, setIsPlaying] = useState(false);
  const [invocationLogPanelOpen, setInvocationLogPanelOpen] = useState(false);
  const [invocationLogCallId, setInvocationLogCallId] = useState(null);
  const [invocationLogCallStartedAt, setInvocationLogCallStartedAt] = useState(null);
  const [invocationLogRecords, setInvocationLogRecords] = useState([]);
  const [loadingInvocationLog, setLoadingInvocationLog] = useState(false);
  /** Selected decoded entry for the debug detail popout: { timeMs, subsystem, entry }. */
  const [debugEntryDetail, setDebugEntryDetail] = useState(null);
  const [manualCallIdInput, setManualCallIdInput] = useState('');
  const audioRef = useRef(null);
  const prevBlobUrlRef = useRef(null);
  const autoplayAfterLoadRef = useRef(false);
  const selectedCallIdRef = useRef(null);
  const invocationLogListRef = useRef(null);
  const invocationLogItemRefs = useRef([]);
  const transcriptScrollRef = useRef(null);

  // "Troubleshoot with AI": gather this call's whole transfer tree (every leg's
  // transcript, function calls and invocation log) and hand it to the Build tab's
  // AI builder — the same context produced after a WebRTC test run.
  const troubleshootCall = useCallback(async (call) => {
    if (!call?.id || !onTroubleshoot) return;
    setTroubleshootingId(call.id);
    try {
      const testResult = await getCallDiagnostics(call.id, call.agentId);
      setTroubleshootingId(null);
      onTroubleshoot({ agentId: call.agentId, testResult });
    } catch (err) {
      setTroubleshootingId(null);
      setError?.(`Couldn't gather call diagnostics: ${err?.message || String(err)}`);
    }
  }, [onTroubleshoot, setError]);

  const renderTroubleshootCell = (call) => (onTroubleshoot ? (
    <td style={{ width: '2.75rem', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
      <IconButton
        size="sm"
        variant="plain"
        color="warning"
        disabled={!!troubleshootingId}
        onClick={(e) => { e.stopPropagation(); troubleshootCall(call); }}
        title="Troubleshoot this call with AI"
        aria-label="Troubleshoot this call with AI"
      >
        {troubleshootingId === call.id ? <CircularProgress size="sm" /> : <AutoAwesomeIcon fontSize="small" />}
      </IconButton>
    </td>
  ) : null);
  const mobileDebugPanelRef = useRef(null);
  /** Prevents feedback when we programmatically scroll the other panel: 'transcript' | 'debug' | null */
  const scrollSyncSourceRef = useRef(null);

  // On mobile, when debug is opened, scroll it into view.
  useEffect(() => {
    const isXs = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width:600px)').matches;
    if (!isXs || !invocationLogPanelOpen) return;
    requestAnimationFrame(() => {
      mobileDebugPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [invocationLogPanelOpen]);

  /** Timecode-based view only when recording is loaded and user has started playback; otherwise show full transcript. */
  const { visibleTranscript, visibleOffsets } = useMemo(() => {
    const timecodeMode = !!audioSrc && !recordingError && playbackStarted;
    if (!timecodeMode || fullTranscriptOffsets.length === 0) {
      return { visibleTranscript: fullTranscriptEntries, visibleOffsets: fullTranscriptOffsets };
    }
    const t = audioCurrentTime;
    const duration = audioDuration;
    const atEndOfRecording = typeof duration === 'number' && duration > 0 && t >= duration - 0.1;
    const vis = [];
    const off = [];
    let hangupIndex = -1;
    for (let i = 0; i < fullTranscriptOffsets.length; i++) {
      if (fullTranscriptOffsets[i] <= t) {
        vis.push(fullTranscriptEntries[i]);
        off.push(fullTranscriptOffsets[i]);
      }
      if (fullTranscriptEntries[i]?.hangup !== undefined) hangupIndex = i;
    }
    if (atEndOfRecording && hangupIndex >= 0 && !vis.some((e) => e?.hangup !== undefined)) {
      vis.push(fullTranscriptEntries[hangupIndex]);
      off.push(fullTranscriptOffsets[hangupIndex]);
    }
    return { visibleTranscript: vis, visibleOffsets: off };
  }, [audioSrc, recordingError, playbackStarted, fullTranscriptEntries, fullTranscriptOffsets, audioCurrentTime, audioDuration]);

  const customSummary = useMemo(
    () => formatCustomRangeCondensed(customStart, customEnd),
    [customStart, customEnd]
  );

  const customOptionLabel = useMemo(() => {
    const s = String(customSummary || '').trim();
    return s ? `Custom ${s}` : 'Custom';
  }, [customSummary]);

  const handleSortToggle = useCallback((field) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, order: 'desc' };
      return { field, order: prev.order === 'desc' ? 'asc' : 'desc' };
    });
  }, []);

  const handleDatePresetChange = useCallback(
    (_event, v) => {
      const next = v ?? '0';
      if (next === 'custom') {
        setCustomRangeOpen(true);
        setDatePreset('custom');
        return;
      }
      prevNonCustomPresetRef.current = next;
      setDatePreset(next);
      setCustomRangeOpen(false);
    },
    []
  );

  const { startDate, endDate } = useMemo(() => {
    if (datePreset === 'custom') {
      return { startDate: customStart, endDate: customEnd };
    }
    // Single-day presets: value is the day offset from today.
    const offset = Number(datePreset) || 0;
    const day = new Date();
    day.setDate(day.getDate() + offset);
    return { startDate: startOfDay(day), endDate: endOfDay(day) };
  }, [datePreset, customStart, customEnd]);

  useEffect(() => {
    if (!selectedAgentId) {
      setCurrentAgentLabel('');
      return;
    }
    let cancelled = false;
    getAgent(selectedAgentId)
      .then((a) => {
        if (cancelled) return;
        const label = (a?.name || 'Unnamed').trim() || selectedAgentId;
        setCurrentAgentLabel(label);
      })
      .catch(() => {
        if (!cancelled) setCurrentAgentLabel(selectedAgentId);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAgentId]);

  useEffect(() => {
    if (callScopeUserTouchedRef.current) return;
    if (!selectedAgentId && callScope !== 'all') {
      setCallScope('all');
    } else if (selectedAgentId && callScope !== 'agent') {
      setCallScope('agent');
    }
  }, [selectedAgentId, callScope]);

  // Group calls into transfer chains: a root call (one with no parent in the
  // loaded set) plus every leg whose `parentId` chains up to it. A single-leg
  // group renders as an ordinary row; a multi-leg group renders as one nested,
  // expandable group. Groups are sorted by the chosen column at the group level
  // (using the root / total span as the representative), legs in transfer order.
  const callGroups = useMemo(() => {
    const rows = Array.isArray(calls) ? calls : [];
    const cmpText = (a, b) => {
      const aa = String(a ?? '').toLowerCase();
      const bb = String(b ?? '').toLowerCase();
      if (aa < bb) return -1;
      if (aa > bb) return 1;
      return 0;
    };
    const byId = new Map(rows.map((c) => [c.id, c]));
    const childrenByParent = new Map();
    rows.forEach((c) => {
      const pid = c.parentId && byId.has(c.parentId) ? c.parentId : null;
      if (!pid) return;
      const arr = childrenByParent.get(pid) || [];
      arr.push(c);
      childrenByParent.set(pid, arr);
    });
    const byStart = (a, b) => (callStartedAtMs(a) ?? 0) - (callStartedAtMs(b) ?? 0);
    const collectLegs = (root) => {
      const out = [];
      const visit = (c) => {
        out.push(c);
        (childrenByParent.get(c.id) || []).slice().sort(byStart).forEach(visit);
      };
      visit(root);
      return out;
    };
    const groups = rows
      .filter((c) => !(c.parentId && byId.has(c.parentId)))
      .map((root) => {
        const legs = collectLegs(root);
        const last = legs[legs.length - 1] || root;
        return {
          root,
          legs,
          isGroup: legs.length > 1,
          rep: {
            startedAt: root.startedAt,
            endedAt: last.endedAt || root.endedAt,
            agentId: root.agentId,
            callerId: root.callerId,
            calledId: root.calledId,
          },
        };
      });
    const byDate = (a, b) => (callStartedAtMs(a) ?? 0) - (callStartedAtMs(b) ?? 0);
    const byDuration = (a, b) => (callDurationSeconds(a) ?? 0) - (callDurationSeconds(b) ?? 0);
    const byAgent = (a, b) => cmpText(
      a?.agentId ? (agentNameById.get(a.agentId) || a.agentId) : '',
      b?.agentId ? (agentNameById.get(b.agentId) || b.agentId) : '',
    );
    const byCaller = (a, b) => cmpText(a?.callerId, b?.callerId);
    const byCallee = (a, b) => cmpText(a?.calledId, b?.calledId);
    const base = (() => {
      if (sort.field === 'duration') return byDuration;
      if (sort.field === 'agent') return byAgent;
      if (sort.field === 'caller') return byCaller;
      if (sort.field === 'callee') return byCallee;
      return byDate; // startedAt
    })();
    groups.sort((ga, gb) => (sort.order === 'desc' ? base(gb.rep, ga.rep) : base(ga.rep, gb.rep)));
    return groups;
  }, [calls, agentNameById, sort.field, sort.order]);

  const toggleGroup = (id) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const agentNameOf = (call) => (call?.agentId ? (agentNameById.get(call.agentId) || call.agentId) : '–');
  const legLabel = (leg) => {
    if (leg?.agentId) return agentNameById.get(leg.agentId) || String(leg.agentId).slice(0, 8);
    if (leg?.modelName && /bridge|telephony/i.test(leg.modelName)) return 'bridge';
    return '–';
  };
  const journeyLabel = (group) => group.legs.map(legLabel).join(' → ');
  const agentCellSx = { fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 };
  const railSx = { borderLeft: '3px solid var(--joy-palette-primary-500)' };
  // Leg rows carry no troubleshoot button (the group header's covers the whole
  // tree), but still need the cell so columns line up — when troubleshoot is on.
  const troubleshootSpacerCell = () => (onTroubleshoot ? <td /> : null);

  const renderDesktopRows = (group) => {
    if (!group.isGroup) {
      const call = group.root;
      return (
        <tr
          key={call.id}
          onClick={() => loadCall(call.id, call.startedAt)}
          style={{ cursor: 'pointer', background: selectedCallId === call.id ? 'var(--joy-palette-background-level1)' : undefined }}
        >
          <td>{formatDateTime(call.startedAt)}</td>
          {callScope === 'all' && <td title={call.agentId || undefined} style={agentCellSx}>{agentNameOf(call)}</td>}
          <td>{call.callerId || '–'}</td>
          <td>{call.calledId || '–'}</td>
          <td>{formatDuration(call.startedAt, call.endedAt)}</td>
          {renderTroubleshootCell(call)}
        </tr>
      );
    }
    const expanded = expandedGroups.has(group.root.id);
    const header = (
      <tr key={group.root.id} onClick={() => toggleGroup(group.root.id)} style={{ cursor: 'pointer', background: 'var(--joy-palette-background-level1)' }}>
        <td style={{ ...railSx, whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            {formatDateTime(group.root.startedAt)}
          </Box>
        </td>
        {callScope === 'all' && (
          <td style={agentCellSx}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <AltRouteIcon sx={{ fontSize: 16, color: 'primary.500', flexShrink: 0 }} />
              <Typography level="body-sm" noWrap sx={{ minWidth: 0 }}>{journeyLabel(group)}</Typography>
              <Chip size="sm" variant="soft" color="primary" sx={{ flexShrink: 0 }}>{group.legs.length} legs</Chip>
            </Box>
          </td>
        )}
        <td>{group.root.callerId || '–'}</td>
        <td>{group.root.calledId || '–'}</td>
        <td>{formatDuration(group.root.startedAt, group.rep.endedAt)}</td>
        {renderTroubleshootCell(group.root)}
      </tr>
    );
    if (!expanded) return header;
    return (
      <React.Fragment key={group.root.id}>
        {header}
        {group.legs.map((leg) => (
          <tr
            key={leg.id}
            onClick={() => loadCall(leg.id, leg.startedAt)}
            style={{ cursor: 'pointer', background: selectedCallId === leg.id ? 'var(--joy-palette-background-level1)' : undefined }}
          >
            <td style={{ ...railSx, whiteSpace: 'nowrap' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1.5, color: 'text.tertiary' }}>
                <SubdirectoryArrowRightIcon sx={{ fontSize: 16 }} />
                {formatDateTime(leg.startedAt)}
              </Box>
            </td>
            {callScope === 'all' && (
              <td style={agentCellSx}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--joy-palette-success-500)', flexShrink: 0 }} />
                  <Typography level="body-sm" noWrap>{legLabel(leg)}</Typography>
                </Box>
              </td>
            )}
            <td />
            <td />
            <td style={{ color: 'var(--joy-palette-text-secondary)' }}>{formatDuration(leg.startedAt, leg.endedAt)}</td>
            {troubleshootSpacerCell()}
          </tr>
        ))}
      </React.Fragment>
    );
  };

  const renderMobileRows = (group) => {
    if (!group.isGroup) {
      const call = group.root;
      const name = agentNameOf(call);
      const agentShort = name && name !== '–' ? String(name).slice(0, 8) : '–';
      return (
        <tr key={call.id} onClick={() => loadCall(call.id, call.startedAt)} style={{ cursor: 'pointer' }}>
          <td style={{ whiteSpace: 'nowrap' }}>{formatMobileDateTimeShort(call.startedAt)}</td>
          <td style={{ fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>{agentShort}</td>
          <td style={{ whiteSpace: 'nowrap', width: '4.5rem', textAlign: 'right' }}>{formatDuration(call.startedAt, call.endedAt)}</td>
          {renderTroubleshootCell(call)}
        </tr>
      );
    }
    const expanded = expandedGroups.has(group.root.id);
    const header = (
      <tr key={group.root.id} onClick={() => toggleGroup(group.root.id)} style={{ cursor: 'pointer', background: 'var(--joy-palette-background-level1)' }}>
        <td style={{ ...railSx, whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            {formatMobileDateTimeShort(group.root.startedAt)}
          </Box>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <AltRouteIcon sx={{ fontSize: 15, color: 'primary.500' }} />
            <Chip size="sm" variant="soft" color="primary">{group.legs.length} legs</Chip>
          </Box>
        </td>
        <td style={{ whiteSpace: 'nowrap', width: '4.5rem', textAlign: 'right' }}>{formatDuration(group.root.startedAt, group.rep.endedAt)}</td>
        {renderTroubleshootCell(group.root)}
      </tr>
    );
    if (!expanded) return header;
    return (
      <React.Fragment key={group.root.id}>
        {header}
        {group.legs.map((leg) => (
          <tr key={leg.id} onClick={() => loadCall(leg.id, leg.startedAt)} style={{ cursor: 'pointer' }}>
            <td style={{ whiteSpace: 'nowrap' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1.5, color: 'text.tertiary' }}>
                <SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />
                {formatMobileDateTimeShort(leg.startedAt)}
              </Box>
            </td>
            <td style={{ fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap', color: 'var(--joy-palette-text-secondary)' }}>{legLabel(leg)}</td>
            <td style={{ whiteSpace: 'nowrap', width: '4.5rem', textAlign: 'right', color: 'var(--joy-palette-text-secondary)' }}>{formatDuration(leg.startedAt, leg.endedAt)}</td>
            {troubleshootSpacerCell()}
          </tr>
        ))}
      </React.Fragment>
    );
  };

  const loadAllCalls = useCallback(async () => {
    setLoadingCalls(true);
    try {
      if (callScope === 'agent') {
        if (!selectedAgentId) {
          setCalls([]);
          return;
        }
        let all = [];
        let offset = 0;
        for (let i = 0; i < 200; i++) {
          const data = await listCallsForAgent(selectedAgentId, { startDate, endDate, offset, limit: CALLS_PAGE_SIZE });
          const rows = Array.isArray(data?.calls) ? data.calls : [];
          all = all.concat(rows);
          if (all.length >= MAX_CALLS_TO_DISPLAY) {
            all = all.slice(0, MAX_CALLS_TO_DISPLAY);
            const msg = `Too many calls to display: only first ${MAX_CALLS_TO_DISPLAY} shown - please narrow your criteria or date range`;
            setError?.(msg);
            break;
          }
          if (data?.next === false || data?.next == null) break;
          offset = Number(data.next) || 0;
          if (rows.length === 0) break;
        }
        setCalls(all);
        return;
      }

      // callScope === 'all' — follow index-based cursor until done
      let all = [];
      let lastIndex = 0;
      for (let i = 0; i < 500; i++) {
        const data = await listAllCalls({ startDate, endDate, lastIndex, limit: CALLS_PAGE_SIZE });
        const rows = Array.isArray(data?.calls) ? data.calls : [];
        all = all.concat(rows);
        if (all.length >= MAX_CALLS_TO_DISPLAY) {
          all = all.slice(0, MAX_CALLS_TO_DISPLAY);
          const msg = `Too many calls to display: only first ${MAX_CALLS_TO_DISPLAY} shown - please narrow your criteria or date range`;
          setError?.(msg);
          break;
        }
        if (data?.next === false || data?.next == null) break;
        const next = Number(data.next);
        if (!Number.isFinite(next) || next <= lastIndex) break;
        lastIndex = next;
        if (rows.length === 0) break;
      }
      setCalls(all);
    } catch (err) {
      setError?.(err?.response?.data?.message || err.message || 'Failed to load calls');
      setCalls([]);
    } finally {
      setLoadingCalls(false);
    }
  }, [callScope, selectedAgentId, startDate, endDate, CALLS_PAGE_SIZE, MAX_CALLS_TO_DISPLAY, setError]);

  useEffect(() => {
    void loadAllCalls();
  }, [callScope, selectedAgentId, startDate, endDate, loadAllCalls]);

  useEffect(() => {
    if (callScope !== 'all') return;
    const ids = [...new Set(calls.map((c) => c?.agentId).filter(Boolean))];
    const missing = ids.filter((id) => !agentNameById.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(missing.slice(0, 25).map((id) => getAgent(id)));
      if (cancelled) return;
      setAgentNameById((prev) => {
        const next = new Map(prev);
        for (let i = 0; i < results.length; i++) {
          const r = results[i];
          const id = missing[i];
          if (r.status === 'fulfilled') {
            const name = (r.value?.name || '').trim();
            next.set(id, name || id);
          } else {
            next.set(id, id);
          }
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [callScope, calls, agentNameById]);

  const loadCall = useCallback(async (callId, callStartedAt) => {
    selectedCallIdRef.current = callId;
    setSelectedCallId(callId);
    setFullTranscriptEntries([]);
    setFullTranscriptOffsets([]);
    setAudioSrc(null);
    setRecordingError(null);
    setLoadingRecording(false);
    setAudioCurrentTime(0);
    setAudioDuration(null);
    setPlaybackStarted(false);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    if (prevBlobUrlRef.current) {
      URL.revokeObjectURL(prevBlobUrlRef.current);
      prevBlobUrlRef.current = null;
    }

    if (!callId) return;

    setLoadingTranscript(true);
    try {
      const records = await getCallTransactionLog(callId);
      const { entries, offsets } = transactionLogToTranscript(records, callStartedAt);
      setFullTranscriptEntries(entries);
      setFullTranscriptOffsets(offsets);
    } catch (err) {
      setError?.(err?.response?.data?.message || err.message || 'Failed to load transcript');
      setFullTranscriptEntries([]);
      setFullTranscriptOffsets([]);
    } finally {
      setLoadingTranscript(false);
    }
  }, [setError]);

  const clearSelectedCall = useCallback(() => {
    selectedCallIdRef.current = null;
    setSelectedCallId(null);
    setFullTranscriptEntries([]);
    setFullTranscriptOffsets([]);
    setLoadingTranscript(false);
    setAudioSrc(null);
    setRecordingError(null);
    setLoadingRecording(false);
    setAudioCurrentTime(0);
    setAudioDuration(null);
    setPlaybackStarted(false);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (prevBlobUrlRef.current) {
      URL.revokeObjectURL(prevBlobUrlRef.current);
      prevBlobUrlRef.current = null;
    }
    setInvocationLogPanelOpen(false);
    setInvocationLogCallId(null);
    setInvocationLogCallStartedAt(null);
    setInvocationLogRecords([]);
    setLoadingInvocationLog(false);
    setDebugEntryDetail(null);
  }, []);

  const selectedCall = useMemo(
    () => (selectedCallId && calls?.length ? calls.find((c) => c.id === selectedCallId) : null),
    [selectedCallId, calls]
  );
  const selectedCallInList = useMemo(
    () => !!(selectedCallId && calls.some((c) => c.id === selectedCallId)),
    [selectedCallId, calls]
  );
  const hasRecording = !!selectedCall?.recordingId;
  /** List omits recordingId for unknown calls; allow a fetch attempt when the call was opened by ID and is not in the current list. */
  const canTryRecordingLoad = !!(selectedCallId && (hasRecording || !selectedCallInList));

  const handleLoadRecording = useCallback(() => {
    if (!selectedCallId || audioSrc || !canTryRecordingLoad) return;
    const callIdForLoad = selectedCallId;
    autoplayAfterLoadRef.current = true;
    setLoadingRecording(true);
    setRecordingError(null);
    getCallRecordingBlob(callIdForLoad)
      .then((url) => {
        if (selectedCallIdRef.current !== callIdForLoad) {
          URL.revokeObjectURL(url);
          return;
        }
        prevBlobUrlRef.current = url;
        setAudioSrc(url);
        setRecordingError(null);
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setRecordingError('No recording for this call');
        } else {
          setRecordingError(err?.response?.data?.message || err.message || 'Failed to load recording');
        }
      })
      .finally(() => setLoadingRecording(false));
  }, [selectedCallId, audioSrc, canTryRecordingLoad]);

  const handlePlay = useCallback(() => {
    setPlaybackStarted(true);
    setIsPlaying(true);
  }, []);
  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const toggleInvocationLog = useCallback((call) => {
    if (!call?.id) return;
    const openForCall = invocationLogCallId === call.id;
    if (invocationLogPanelOpen && openForCall) {
      setInvocationLogPanelOpen(false);
      setInvocationLogCallId(null);
      setInvocationLogCallStartedAt(null);
      setInvocationLogRecords([]);
      return;
    }
    setInvocationLogPanelOpen(true);
    if (invocationLogCallId === call.id && invocationLogRecords.length > 0) {
      return;
    }
    setInvocationLogCallId(call.id);
    setInvocationLogCallStartedAt(call.startedAt || null);
    setLoadingInvocationLog(true);
    getCallInvocationLog(call.id)
      .then((records) => setInvocationLogRecords(Array.isArray(records) ? records : []))
      .catch(() => setInvocationLogRecords([]))
      .finally(() => setLoadingInvocationLog(false));
  }, [invocationLogPanelOpen, invocationLogCallId, invocationLogRecords.length]);

  const invocationLogStartMs = invocationLogCallStartedAt ? new Date(invocationLogCallStartedAt).getTime() : 0;
  const syncWithTranscript = invocationLogCallId === selectedCallId && !!audioSrc && playbackStarted;
  /** Decoded: merge all subsystem log.logs, sort by time; then filter by currentTime when syncing. */
  const mergedEntries = useMemo(
    () => decodeInvocationLogRecords(invocationLogRecords),
    [invocationLogRecords]
  );
  const { visibleInvocationLogRecords, activeInvocationLogIndex } = useMemo(() => {
    if (mergedEntries.length === 0) {
      return { visibleInvocationLogRecords: [], activeInvocationLogIndex: -1 };
    }
    const offsets = mergedEntries.map((item) => (item.timeMs - invocationLogStartMs) / 1000);
    if (!syncWithTranscript || audioCurrentTime == null) {
      return {
        visibleInvocationLogRecords: mergedEntries,
        activeInvocationLogIndex: -1,
      };
    }
    const t = audioCurrentTime;
    const vis = [];
    for (let i = 0; i < offsets.length; i++) {
      if (offsets[i] <= t) {
        vis.push(mergedEntries[i]);
      }
    }
    return {
      visibleInvocationLogRecords: vis,
      activeInvocationLogIndex: vis.length - 1,
    };
  }, [mergedEntries, invocationLogStartMs, syncWithTranscript, audioCurrentTime]);

  /** During playback, scroll the debug panel directly from timecode (active log entry); only when index changes to avoid jitter. */
  useEffect(() => {
    if (!syncWithTranscript) return;
    if (activeInvocationLogIndex >= 0 && invocationLogItemRefs.current[activeInvocationLogIndex]) {
      invocationLogItemRefs.current[activeInvocationLogIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [syncWithTranscript, activeInvocationLogIndex]);

  /** Only sync scroll by ratio when playback is not actively driving both panels (paused or not started); during play each panel is driven by timecode. */
  const scrollSyncEnabled =
    invocationLogPanelOpen &&
    invocationLogCallId === selectedCallId &&
    visibleInvocationLogRecords.length > 0 &&
    visibleTranscript.length > 0 &&
    (!syncWithTranscript || !isPlaying);
  const handleTranscriptScroll = useCallback(() => {
    if (!scrollSyncEnabled || scrollSyncSourceRef.current === 'transcript' || scrollSyncSourceRef.current === 'debug') {
      if (scrollSyncSourceRef.current) scrollSyncSourceRef.current = null;
      return;
    }
    const el = transcriptScrollRef.current;
    const debugEl = invocationLogListRef.current;
    if (!el || !debugEl) return;
    const ratio = el.scrollHeight <= el.clientHeight ? 0 : el.scrollTop / (el.scrollHeight - el.clientHeight);
    scrollSyncSourceRef.current = 'transcript';
    debugEl.scrollTop = ratio * Math.max(0, debugEl.scrollHeight - debugEl.clientHeight);
  }, [scrollSyncEnabled]);

  const handleDebugScroll = useCallback(() => {
    if (!scrollSyncEnabled || scrollSyncSourceRef.current === 'debug' || scrollSyncSourceRef.current === 'transcript') {
      if (scrollSyncSourceRef.current === 'debug' || scrollSyncSourceRef.current === 'transcript') scrollSyncSourceRef.current = null;
      return;
    }
    const el = invocationLogListRef.current;
    const transcriptEl = transcriptScrollRef.current;
    if (!el || !transcriptEl) return;
    const ratio = el.scrollHeight <= el.clientHeight ? 0 : el.scrollTop / (el.scrollHeight - el.clientHeight);
    scrollSyncSourceRef.current = 'debug';
    transcriptEl.scrollTop = ratio * Math.max(0, transcriptEl.scrollHeight - transcriptEl.clientHeight);
  }, [scrollSyncEnabled]);

  useEffect(() => {
    return () => {
      if (prevBlobUrlRef.current) URL.revokeObjectURL(prevBlobUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!audioSrc || !autoplayAfterLoadRef.current || !audioRef.current) return;
    autoplayAfterLoadRef.current = false;
    const el = audioRef.current;
    el.play().catch(() => {});
  }, [audioSrc]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) setAudioCurrentTime(audioRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current && Number.isFinite(audioRef.current.duration)) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const showAgentHelp = !selectedAgentId && callScope === 'agent';
  const noAgent = false;

  const handleFindCallById = useCallback(async () => {
    const id = manualCallIdInput.trim();
    if (!id) return;
    setInvocationLogPanelOpen(true);
    setInvocationLogCallId(id);
    setInvocationLogCallStartedAt(null);
    setInvocationLogRecords([]);
    setLoadingInvocationLog(true);
    getCallInvocationLog(id)
      .then((records) => setInvocationLogRecords(Array.isArray(records) ? records : []))
      .catch(() => setInvocationLogRecords([]))
      .finally(() => setLoadingInvocationLog(false));
    await loadCall(id, null);
  }, [manualCallIdInput, loadCall]);

  // When a specific call is requested (e.g. from Numbering tab), load it once calls are available
  useEffect(() => {
    if (!observeCallId || !calls.length) return;
    const target = calls.find((c) => c.id === observeCallId);
    if (target) {
      loadCall(target.id, target.startedAt);
      setObserveCallId?.(null);
    }
  }, [observeCallId, calls, loadCall, setObserveCallId]);

  const mobileView = selectedCallId ? (
    <Box sx={{ py: 2, display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="sm"
              variant="outlined"
              startDecorator={<ArrowBackIcon />}
              onClick={clearSelectedCall}
            >
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup
              variant="outlined"
              size="sm"
              value={invocationLogPanelOpen && invocationLogCallId === selectedCallId ? 'debug' : ''}
              onChange={() =>
                toggleInvocationLog(
                  calls.find((c) => c.id === selectedCallId) || { id: selectedCallId, startedAt: null }
                )}
              aria-label="Debug log"
            >
              <Button value="debug" startDecorator={<BugReportIcon />}>
                Debug
              </Button>
            </ToggleButtonGroup>
          </Box>

          <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Recording
            </Typography>
            {recordingError && (
              <Typography level="body2" color="neutral">
                {recordingError}
              </Typography>
            )}
            {(selectedCallId && canTryRecordingLoad) || audioSrc ? (
              <Box
                onClick={!audioSrc && !loadingRecording ? handleLoadRecording : undefined}
                onKeyDown={
                  !audioSrc && !loadingRecording
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleLoadRecording();
                        }
                      }
                    : undefined
                }
                role={!audioSrc && !loadingRecording ? 'button' : undefined}
                tabIndex={!audioSrc && !loadingRecording ? 0 : undefined}
                sx={{
                  position: 'relative',
                  width: '100%',
                  cursor: !audioSrc && !loadingRecording ? 'pointer' : undefined,
                }}
              >
                <Box
                  component="audio"
                  ref={audioRef}
                  src={audioSrc || undefined}
                  controls
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onEnded={handlePause}
                  onTimeUpdate={handleTimeUpdate}
                  onSeeked={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  sx={{
                    width: '100%',
                    pointerEvents: audioSrc ? 'auto' : 'none',
                    opacity: 1,
                  }}
                />
                {loadingRecording && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'background.backdrop',
                      borderRadius: 'sm',
                    }}
                  >
                    <CircularProgress size="sm" />
                  </Box>
                )}
              </Box>
            ) : null}
            {!audioSrc && !loadingRecording && !recordingError && selectedCallId && !canTryRecordingLoad && (
              <Typography level="body2" color="neutral">
                No recording for this call.
              </Typography>
            )}
          </Sheet>

          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
            {loadingTranscript && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            )}
            {!loadingTranscript && (
              <Box sx={{ minHeight: 240, maxHeight: '55vh', overflow: 'auto' }}>
                <Transcript
                  ref={transcriptScrollRef}
                  transcript={visibleTranscript}
                  number={null}
                  tooltip={null}
                  state="initial"
                  audioModel={false}
                  disconnect={() => {}}
                  segmentOffsets={visibleOffsets}
                  currentTime={audioCurrentTime}
                  hideAgentStatus
                  scrollToEndWhenLastIsHangup
                  noBorder
                  onScroll={handleTranscriptScroll}
                />
              </Box>
            )}
            {!loadingTranscript && fullTranscriptEntries.length === 0 && selectedCallId && (
              <Typography level="body2" color="neutral" sx={{ p: 2 }}>
                No transcript for this call.
              </Typography>
            )}
          </Sheet>

          {invocationLogPanelOpen && (
            <Sheet ref={mobileDebugPanelRef} variant="outlined" sx={{ borderRadius: 'sm', p: 2, overflow: 'hidden' }}>
              <Typography level="title-sm" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BugReportIcon fontSize="small" />
                Debug
              </Typography>
              {loadingInvocationLog && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size="sm" />
                </Box>
              )}
              {!loadingInvocationLog && visibleInvocationLogRecords.length === 0 && (
                <Typography level="body2" color="neutral">
                  No debug log.
                </Typography>
              )}
              {!loadingInvocationLog && visibleInvocationLogRecords.length > 0 && (
                <Box ref={invocationLogListRef} sx={{ maxHeight: 240, overflow: 'auto' }} onScroll={handleDebugScroll}>
                  {visibleInvocationLogRecords.map((item, index) => {
                    const isActive = syncWithTranscript && index === activeInvocationLogIndex;
                    return (
                      <Box
                        key={`${item.timeMs}-${index}`}
                        ref={(el) => { invocationLogItemRefs.current[index] = el; }}
                        onClick={() => setDebugEntryDetail(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDebugEntryDetail(item);
                          }
                        }}
                        sx={{
                          py: 0.5,
                          px: 1,
                          borderRadius: 'sm',
                          fontSize: 'xs',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          ...(isActive ? { bgcolor: 'action.selected' } : {}),
                        }}
                      >
                        <Typography level="body3" color="neutral">
                          {formatLocalTimeHmsMs(item.timeMs)}
                          {item.subsystem ? ` · ${item.subsystem}` : ''}
                        </Typography>
                        <Typography level="body3" sx={{ wordBreak: 'break-word' }}>
                          {decodedEntrySummary(item)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Sheet>
          )}
        </Box>
  ) : (
      <Box sx={{ py: 2, display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 2 }}>
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Date range
            </Typography>
            <Select
              value={datePreset}
              onChange={handleDatePresetChange}
              size="sm"
              sx={{ width: '100%' }}
            >
              {datePresets.map((p) => (
                <Option
                  key={p.value}
                  value={p.value}
                  onMouseDown={
                    p.value === 'custom'
                      ? () => {
                          // If "Custom" is already selected, onChange won't fire—still allow edit.
                          if (datePreset === 'custom') setCustomRangeOpen(true);
                        }
                      : undefined
                  }
                >
                  {p.value === 'custom' ? customOptionLabel : p.label}
                </Option>
              ))}
            </Select>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography level="body-sm" textColor="neutral.700">
                All agents
              </Typography>
              <Tooltip
                title={!selectedAgentId ? 'Load the agent to see calls for just one agent' : ''}
                placement="top"
                variant="soft"
              >
                <span>
                  <Switch
                    size="sm"
                    checked={callScope === 'all'}
                    disabled={!selectedAgentId}
                    onChange={(e) => {
                      callScopeUserTouchedRef.current = true;
                      setCallScope(e.target.checked ? 'all' : 'agent');
                    }}
                    slotProps={{ input: { 'aria-label': 'Show calls for all agents' } }}
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Sheet>

        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
          {loadingCalls && (
            <Box sx={{ p: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          )}
          {!loadingCalls && calls.length === 0 && (
            <Typography level="body2" color="neutral" sx={{ p: 2 }}>
              No calls in this range.
            </Typography>
          )}
          {!loadingCalls && calls.length > 0 && (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Table size="sm" sx={{ '--TableCell-paddingX': '0.75rem' }}>
                <tbody>
                  {callGroups.map((group) => renderMobileRows(group))}
                </tbody>
              </Table>
            </Box>
          )}
        </Sheet>
      </Box>
  );

  return (
    <>
      {mobileView}
      <Modal
        open={customRangeOpen}
        onClose={() => {
          setCustomRangeOpen(false);
          // If user opened Custom but doesn't confirm a first-time custom range,
          // revert to the previous non-custom selection.
          if (datePreset === 'custom' && !hasAppliedCustom) {
            setDatePreset(prevNonCustomPresetRef.current || '0');
          }
        }}
      >
        <ModalDialog sx={{ width: '100%', maxWidth: 520 }}>
          <ModalClose />
          <DialogTitle>Custom date range</DialogTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Desktop: date + time inputs (calendar picker). Mobile: datetime-local. */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography level="title-sm" sx={{ mb: 0.5 }}>
                Start
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Input
                  type="date"
                  value={toDateInputValue(customStart)}
                  onChange={(e) => setCustomStart((prev) => setDatePart(prev, e.target.value))}
                  sx={{ flex: '1 1 180px' }}
                />
                <Input
                  type="time"
                  value={toTimeInputValue(customStart)}
                  onChange={(e) => setCustomStart((prev) => setTimePart(prev, e.target.value, { endOfMinute: false }))}
                  sx={{ width: 140 }}
                />
              </Box>
              <Typography level="title-sm" sx={{ mb: 0.5, mt: 1.5 }}>
                End
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Input
                  type="date"
                  value={toDateInputValue(customEnd)}
                  onChange={(e) => setCustomEnd((prev) => setDatePart(prev, e.target.value))}
                  sx={{ flex: '1 1 180px' }}
                />
                <Input
                  type="time"
                  value={toTimeInputValue(customEnd)}
                  onChange={(e) => setCustomEnd((prev) => setTimePart(prev, e.target.value, { endOfMinute: true }))}
                  sx={{ width: 140 }}
                />
              </Box>
            </Box>

            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <Box>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>
                  Start
                </Typography>
                <Input
                  type="datetime-local"
                  value={toDatetimeLocalValue(customStart)}
                  onChange={(e) => {
                    const next = fromDatetimeLocalValue(e.target.value, customStart);
                    next.setSeconds(0, 0);
                    setCustomStart(next);
                  }}
                  slotProps={{ input: { step: 60 } }}
                />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>
                  End
                </Typography>
                <Input
                  type="datetime-local"
                  value={toDatetimeLocalValue(customEnd)}
                  onChange={(e) => {
                    const next = fromDatetimeLocalValue(e.target.value, customEnd);
                    // treat selected minute as "end of minute"
                    next.setSeconds(59, 999);
                    setCustomEnd(next);
                  }}
                  slotProps={{ input: { step: 60 } }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 0.5 }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => {
                  setCustomRangeOpen(false);
                  if (datePreset === 'custom' && !hasAppliedCustom) {
                    setDatePreset(prevNonCustomPresetRef.current || '0');
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                onClick={() => {
                  // Normalize defaults: if user hasn't touched times, keep them at 00:00 and 23:59:59.
                  const s = new Date(customStart);
                  const e = new Date(customEnd);
                  if (e < s) {
                    // Swap to keep range valid.
                    setCustomStart(e);
                    setCustomEnd(s);
                  }
                  setDatePreset('custom');
                  setHasAppliedCustom(true);
                  setCustomRangeOpen(false);
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography level="h4">Observe</Typography>
        {selectedCallId && (
          <ToggleButtonGroup
            variant="outlined"
            size="sm"
            value={invocationLogPanelOpen && invocationLogCallId === selectedCallId ? 'debug' : ''}
            onChange={() =>
              toggleInvocationLog(
                calls.find((c) => c.id === selectedCallId) || { id: selectedCallId, startedAt: null }
              )}
            aria-label="Debug log"
          >
            <Button value="debug" startDecorator={<BugReportIcon />}>
              Debug
            </Button>
          </ToggleButtonGroup>
        )}
      </Box>

      {showAgentHelp && (
        <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm' }}>
          <Typography level="body1" sx={{ lineHeight: 1.7 }}>
            No agent selected. Switch to <strong>All calls</strong> above to see
            every call across your agents.
          </Typography>
        </Sheet>
      )}

      {!noAgent && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: '320px minmax(0, 1fr)',
            gridTemplateColumns: '1fr',
            gap: 2,
            mt: 2,
            height: 'calc(100vh - 220px)',
            minHeight: 360,
          }}
        >
          {/* Row 1: strictly 320px – grid prevents this row from growing; overflow hidden so content cannot push */}
          <Box
            sx={{
              minHeight: 0,
              maxHeight: '320px',
              overflow: 'hidden',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: invocationLogPanelOpen ? '1fr 2fr minmax(260px, 1fr)' : '1fr 2fr',
              },
              gap: 2,
            }}
          >
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography level="title-sm" sx={{ mb: 1 }}>Date range</Typography>
                <Select
                  value={datePreset}
                  onChange={handleDatePresetChange}
                  size="sm"
                  sx={{ width: '100%' }}
                >
                  {datePresets.map((p) => (
                    <Option
                      key={p.value}
                      value={p.value}
                      onMouseDown={
                        p.value === 'custom'
                          ? () => {
                              // If "Custom" is already selected, onChange won't fire—still allow edit.
                              if (datePreset === 'custom') setCustomRangeOpen(true);
                            }
                          : undefined
                      }
                    >
                      {p.value === 'custom' ? customOptionLabel : p.label}
                    </Option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography level="body-sm" textColor="neutral.700">
                    All agents
                  </Typography>
                  <Tooltip
                    title={!selectedAgentId ? 'Load the agent to see calls for just one agent' : ''}
                    placement="top"
                    variant="soft"
                  >
                    <span>
                      <Switch
                        size="sm"
                        checked={callScope === 'all'}
                        disabled={!selectedAgentId}
                        onChange={(e) => {
                          callScopeUserTouchedRef.current = true;
                          setCallScope(e.target.checked ? 'all' : 'agent');
                        }}
                        slotProps={{ input: { 'aria-label': 'Show calls for all agents' } }}
                      />
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Call ID</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <Input
                    size="sm"
                    placeholder="Paste call ID"
                    value={manualCallIdInput}
                    onChange={(e) => setManualCallIdInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleFindCallById();
                      }
                    }}
                    sx={{ flex: '1 1 120px', minWidth: 0 }}
                  />
                  <Button size="sm" onClick={handleFindCallById}>
                    Find Call ID
                  </Button>
                </Box>
              </Box>
              <Box sx={{ mt: 'auto' }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Recording</Typography>
                {recordingError && (
                  <Typography level="body2" color="neutral">{recordingError}</Typography>
                )}
                {(selectedCallId && canTryRecordingLoad) || audioSrc ? (
                  <Box
                    onClick={!audioSrc && !loadingRecording ? handleLoadRecording : undefined}
                    onKeyDown={
                      !audioSrc && !loadingRecording
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleLoadRecording();
                            }
                          }
                        : undefined
                    }
                    role={!audioSrc && !loadingRecording ? 'button' : undefined}
                    tabIndex={!audioSrc && !loadingRecording ? 0 : undefined}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      cursor: !audioSrc && !loadingRecording ? 'pointer' : undefined,
                    }}
                  >
                    <Box
                      component="audio"
                      ref={audioRef}
                      src={audioSrc || undefined}
                      controls
                      onPlay={handlePlay}
                      onPause={handlePause}
                      onEnded={handlePause}
                      onTimeUpdate={handleTimeUpdate}
                      onSeeked={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      sx={{
                        width: '100%',
                        pointerEvents: audioSrc ? 'auto' : 'none',
                        opacity: 1,
                      }}
                    />
                    {loadingRecording && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'background.backdrop',
                          borderRadius: 'sm',
                        }}
                      >
                        <CircularProgress size="sm" />
                      </Box>
                    )}
                  </Box>
                ) : null}
                {!audioSrc && !loadingRecording && !recordingError && selectedCallId && !canTryRecordingLoad && (
                  <Typography level="body2" color="neutral">No recording for this call.</Typography>
                )}
                {!selectedCallId && (
                  <Typography level="body2" color="neutral">Select a call below to play recording.</Typography>
                )}
              </Box>
            </Sheet>
            <Box sx={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {loadingTranscript && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size="sm" /></Box>}
              {!loadingTranscript && (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <Transcript
                    ref={transcriptScrollRef}
                    transcript={visibleTranscript}
                    number={null}
                    tooltip={null}
                    state="initial"
                    audioModel={false}
                    disconnect={() => {}}
                    segmentOffsets={visibleOffsets}
                    currentTime={audioCurrentTime}
                    hideAgentStatus
                    scrollToEndWhenLastIsHangup
                    noBorder
                    onScroll={handleTranscriptScroll}
                  />
                </Box>
              )}
              {!loadingTranscript && fullTranscriptEntries.length === 0 && selectedCallId && (
                <Typography level="body2" color="neutral" sx={{ p: 2 }}>No transcript for this call.</Typography>
              )}
              {!loadingTranscript && !selectedCallId && (
                <Typography level="body2" color="neutral" sx={{ p: 2 }}>Select a call below to view transcript.</Typography>
              )}
            </Box>
            {invocationLogPanelOpen && (
              <Box sx={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderLeft: 1, borderColor: 'divider', pl: 2 }}>
                <Typography level="title-sm" sx={{ mb: 1, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BugReportIcon fontSize="small" />
                  Debug
                  {invocationLogCallId && invocationLogCallId !== selectedCallId && (
                    <Typography component="span" level="body2" color="neutral" sx={{ ml: 1 }}>
                      (other call)
                    </Typography>
                  )}
                </Typography>
                {loadingInvocationLog && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size="sm" /></Box>}
                {!loadingInvocationLog && visibleInvocationLogRecords.length === 0 && (
                  <Typography level="body2" color="neutral">No debug log.</Typography>
                )}
                {!loadingInvocationLog && visibleInvocationLogRecords.length > 0 && (
                  <Box ref={invocationLogListRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} onScroll={handleDebugScroll}>
                    {visibleInvocationLogRecords.map((item, index) => {
                      const isActive = syncWithTranscript && index === activeInvocationLogIndex;
                      return (
                        <Box
                          key={`${item.timeMs}-${index}`}
                          ref={(el) => { invocationLogItemRefs.current[index] = el; }}
                          onClick={() => setDebugEntryDetail(item)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDebugEntryDetail(item); } }}
                          sx={{
                            py: 0.5,
                            px: 1,
                            borderRadius: 'sm',
                            fontSize: 'xs',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            ...(isActive ? { bgcolor: 'action.selected' } : {}),
                          }}
                        >
                          <Typography level="body3" color="neutral">
                            {formatLocalTimeHmsMs(item.timeMs)}
                            {item.subsystem ? ` · ${item.subsystem}` : ''}
                          </Typography>
                          <Typography level="body3" sx={{ wordBreak: 'break-word' }}>
                            {decodedEntrySummary(item)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Row 2: remaining height – scrollable calls list */}
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Typography level="title-sm" sx={{ p: 2, pb: 0, flexShrink: 0 }}>
              {callScope === 'all'
                ? 'All Calls'
                : `Calls to ${currentAgentLabel || selectedAgentId || '–'}`}
            </Typography>
            {loadingCalls && <Box sx={{ p: 2 }}><CircularProgress size="sm" /></Box>}
            {!loadingCalls && calls.length === 0 && (
              <Typography level="body2" color="neutral" sx={{ p: 2 }}>No calls in this range.</Typography>
            )}
            {!loadingCalls && calls.length > 0 && (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table stickyHeader size="sm" sx={{ '--TableCell-paddingX': '0.75rem' }}>
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSortToggle('startedAt')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                          Date / time
                          {sort.field === 'startedAt' && (sort.order === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />)}
                        </Box>
                      </th>
                      {callScope === 'all' && (
                        <th
                          onClick={() => handleSortToggle('agent')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                            Agent
                            {sort.field === 'agent' && (sort.order === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />)}
                          </Box>
                        </th>
                      )}
                      <th
                        onClick={() => handleSortToggle('caller')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                          Caller
                          {sort.field === 'caller' && (sort.order === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />)}
                        </Box>
                      </th>
                      <th
                        onClick={() => handleSortToggle('callee')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                          Callee
                          {sort.field === 'callee' && (sort.order === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />)}
                        </Box>
                      </th>
                      <th
                        onClick={() => handleSortToggle('duration')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                          Duration
                          {sort.field === 'duration' && (sort.order === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />)}
                        </Box>
                      </th>
                      <th aria-label="Troubleshoot" style={{ width: '2.75rem' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {callGroups.map((group) => renderDesktopRows(group))}
                  </tbody>
                </Table>
              </Box>
            )}
          </Sheet>
        </Box>
      )}

      <Modal open={!!debugEntryDetail} onClose={() => setDebugEntryDetail(null)}>
        <ModalDialog sx={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <ModalClose />
          <DialogTitle>Debug log entry</DialogTitle>
          {debugEntryDetail && (
            <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
              <Typography level="body2" color="neutral" sx={{ mb: 1 }}>
                Time: {formatLocalDateTimeHmsMs(debugEntryDetail.timeMs)}
                {debugEntryDetail.subsystem && ` · ${debugEntryDetail.subsystem}`}
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 1,
                  borderRadius: 'sm',
                  bgcolor: 'neutral.softBg',
                  fontSize: 'xs',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(debugEntryDetail.entry, null, 2)}
              </Box>
            </Box>
          )}
        </ModalDialog>
      </Modal>
        </Box>
      </Box>
    </>
  );
}
